import { FC, ReactNode, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import toast from "react-hot-toast";

import { clsxMerge } from "shared/lib/helpers";
import { LucideIconWithGradient } from "shared/ui/icons/lucide";
import { Button } from "shared/ui/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "shared/ui/shadcn/dialog";

import { GetDailyTodosResponseI } from "@/api/routes/daily-todos";

import { DailyTimeSelect } from "../components/daily-time-control";
import { SnoozePicker } from "../components/snooze-picker";
import {
  useDismissDailyTodos,
  useMarkDailyTodosBriefingSeen,
  useSaveDailyTime,
} from "../data/queries";
import { describeDailyTodosFailure } from "../lib/data";
import {
  describeTodoCounts,
  GATE_LAYERS,
  GateLayerT,
  getBrowserTimeZone,
  getDailyTimeOptions,
  getDefaultDailyTime,
  getGreeting,
  getRemaining,
  getTimeZoneLabel,
} from "../lib/schedule";

/**
 * The daily To-Dos gate's dialog (C-16 S6, C16-D474), one layer at a time.
 * Command Center's welcome popup, tutorial, time ping, snooze and locked
 * modal, in the user app's dialog: the list itself lives on the To-Dos page,
 * so "Let's go" goes there. The intro, the reminder and must-finish can't be
 * closed with ✕, Esc or a click outside (CC's modal couldn't either); the
 * briefing can.
 */

const BRAND_START = "#4B61AB";
const BRAND_STOP = "#CC3997";

const INTRO_POINTS = [
  "Every day: today's and tomorrow's meetings to confirm, and the no-shows and cancels to rebook.",
  "Open a card to work it. Calls, emails and callbacks you log in the app count too.",
  "These leads already showed intent: they're your closest deals. Work the whole list each day.",
];

const Shell: FC<{
  title: string;
  description: ReactNode;
  isClosable: boolean;
  onClose?: () => void;
  footer?: ReactNode;
  children?: ReactNode;
}> = ({ title, description, isClosable, onClose, footer, children }) => (
  <Dialog
    open
    onOpenChange={(open) => {
      if (!open && isClosable) {
        onClose?.();
      }
    }}
  >
    <DialogContent
      onEscapeKeyDown={(event) => {
        if (!isClosable) {
          event.preventDefault();
        }
      }}
      onInteractOutside={(event) => {
        if (!isClosable) {
          event.preventDefault();
        }
      }}
      className={clsxMerge(
        "flex w-[calc(100vw-32px)] max-w-[480px] flex-col gap-0 overflow-hidden rounded-[20px] border-black/[0.08] p-0",
        // The shared dialog always draws a ✕ as its last child.
        !isClosable && "[&>button:last-child]:hidden"
      )}
    >
      <span aria-hidden className="app-gradient h-[3px] w-full shrink-0" />

      <header className="flex flex-col gap-2 px-6 pb-2 pr-12 pt-[21px]">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[#CC3997]/20 bg-white px-2.5 py-1">
          <LucideIconWithGradient
            aria-hidden
            icon={ClipboardCheck}
            startColor={BRAND_START}
            stopColor={BRAND_STOP}
            size={14}
            strokeWidth={2}
          />
          <span className="text-gradient typography-body-5-semibold">
            To-Dos
          </span>
        </span>
        <DialogTitle className="typography-header-7-semibold">
          {title}
        </DialogTitle>
        <DialogDescription className="text-black/70 typography-body-4">
          {description}
        </DialogDescription>
      </header>

      {children && <div className="px-6 py-3">{children}</div>}

      {footer ? (
        <footer className="flex flex-wrap items-center justify-end gap-2 px-6 pb-6 pt-3">
          {footer}
        </footer>
      ) : (
        <div className="pb-3" />
      )}
    </DialogContent>
  </Dialog>
);

const IntroLayer: FC<{ onDone: () => void; onLater: () => void }> = ({
  onDone,
  onLater,
}) => {
  const [step, setStep] = useState<"what" | "time">("what");
  const timeZone = getBrowserTimeZone();
  const options = getDailyTimeOptions(timeZone);
  // 9:00 AM, or the first time this zone can have (zones far ahead of ET).
  const [dailyTime, setDailyTime] = useState(() =>
    getDefaultDailyTime(options)
  );
  const [hasFailed, setHasFailed] = useState(false);
  const save = useSaveDailyTime();
  const briefing = useMarkDailyTodosBriefingSeen();
  const zoneLabel = getTimeZoneLabel(timeZone);

  if (step === "what") {
    return (
      <Shell
        title="Your daily to-dos are here"
        description="Command Center's “To-Do's” now live in the app, under Leads → To-Dos."
        isClosable={false}
        footer={<Button onClick={() => setStep("time")}>Next</Button>}
      >
        <ul className="flex flex-col gap-2">
          {INTRO_POINTS.map((point) => (
            <li
              key={point}
              className="relative pl-3.5 text-black/70 typography-body-4"
            >
              <span
                aria-hidden
                className="absolute left-0 top-[0.6em] size-1 rounded-full bg-black/30"
              />
              {point}
            </li>
          ))}
        </ul>
      </Shell>
    );
  }

  const done = () => {
    briefing.mutate();
    onDone();
  };

  const onSave = () =>
    save.mutate(
      { daily_time: dailyTime, time_zone: timeZone, intro_done: true },
      {
        onSuccess: done,
        onError: (error) => {
          setHasFailed(true);
          toast.error(
            describeDailyTodosFailure(error, "Failed to save the daily time")
          );
        },
      }
    );

  return (
    <Shell
      title="When should we remind you?"
      description={`Pick when your to-dos come up each day${
        zoneLabel ? ` (${zoneLabel})` : ""
      }. If you have dial blocks, the reminder waits until an hour after your last one. You can change it on the To-Dos page.`}
      isClosable={false}
      footer={
        <>
          {hasFailed ? (
            <Button variant="ghost" onClick={onLater}>
              Later
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setStep("what")}>
              Back
            </Button>
          )}
          <Button onClick={onSave} loading={save.isPending}>
            Open To-Dos
          </Button>
        </>
      }
    >
      <DailyTimeSelect
        value={dailyTime}
        onChange={setDailyTime}
        options={options}
        timeZoneLabel={zoneLabel}
      />
    </Shell>
  );
};

const BriefingLayer: FC<{
  todos: GetDailyTodosResponseI;
  firstName?: string;
  now: Date;
  onGo: () => void;
  onLater: () => void;
}> = ({ todos, firstName, now, onGo, onLater }) => {
  const briefing = useMarkDailyTodosBriefingSeen();

  const close = (then: () => void) => {
    briefing.mutate();
    then();
  };

  return (
    <Shell
      title={`${getGreeting(now)}${firstName ? `, ${firstName}` : ""}`}
      description={`You have ${describeTodoCounts(todos)} to work today.`}
      isClosable
      onClose={() => close(onLater)}
      footer={
        <>
          <Button variant="ghost" onClick={() => close(onLater)}>
            Later
          </Button>
          <Button onClick={() => close(onGo)}>Open To-Dos</Button>
        </>
      }
    />
  );
};

const ReminderLayer: FC<{
  todos: GetDailyTodosResponseI;
  now: Date;
  isMustFinish: boolean;
  onGo: () => void;
  isSnoozingInitially?: boolean;
}> = ({ todos, now, isMustFinish, onGo, isSnoozingInitially = false }) => {
  const [isSnoozing, setIsSnoozing] = useState(isSnoozingInitially);
  const dismiss = useDismissDailyTodos();
  const remaining = getRemaining(todos);
  const left = `${remaining} of ${todos.state.total} to-dos left`;

  if (isMustFinish) {
    return (
      <Shell
        title="Your snooze is up"
        description={`${left} today. Work the rest of the list to keep going: these are your closest deals.`}
        isClosable={false}
        footer={<Button onClick={onGo}>Let&apos;s go</Button>}
      />
    );
  }

  if (isSnoozing) {
    return (
      <Shell
        title="Snooze your to-dos"
        description={`${left}. Pick when we should remind you again.`}
        isClosable={false}
      >
        <SnoozePicker
          now={now}
          onBack={() => setIsSnoozing(false)}
          onSnoozed={() => setIsSnoozing(false)}
        />
      </Shell>
    );
  }

  const onStop = () =>
    dismiss.mutate(undefined, {
      onError: (error) =>
        toast.error(
          describeDailyTodosFailure(error, "Failed to stop for today")
        ),
    });

  return (
    <Shell
      title="It's time to work your to-dos"
      description={`${left} today. They're waiting on the To-Dos page.`}
      isClosable={false}
      footer={
        <>
          {todos.state.can_snooze && (
            <Button variant="ghost" onClick={() => setIsSnoozing(true)}>
              Snooze
            </Button>
          )}
          {!todos.state.can_snooze && todos.state.can_dismiss && (
            <Button
              variant="ghost"
              onClick={onStop}
              loading={dismiss.isPending}
            >
              Stop for today
            </Button>
          )}
          <Button onClick={onGo}>Let&apos;s go</Button>
        </>
      }
    />
  );
};

export const DailyTodosGateDialog: FC<{
  layer: GateLayerT;
  todos: GetDailyTodosResponseI | null;
  firstName?: string;
  now: Date;
  onGo: () => void;
  onBriefingLater: () => void;
  /** The intro couldn't be saved: let the caller go on for now. */
  onIntroLater?: () => void;
  /** The harness opens the reminder on its snooze step. */
  isSnoozingInitially?: boolean;
}> = ({
  layer,
  todos,
  firstName,
  now,
  onGo,
  onBriefingLater,
  onIntroLater = () => {},
  isSnoozingInitially,
}) => {
  if (!todos || layer === GATE_LAYERS.NONE) {
    return null;
  }

  switch (layer) {
    case GATE_LAYERS.INTRO:
      return <IntroLayer onDone={onGo} onLater={onIntroLater} />;
    case GATE_LAYERS.BRIEFING:
      return (
        <BriefingLayer
          todos={todos}
          firstName={firstName}
          now={now}
          onGo={onGo}
          onLater={onBriefingLater}
        />
      );
    case GATE_LAYERS.REMINDER:
    case GATE_LAYERS.MUST_FINISH:
      return (
        <ReminderLayer
          key={layer}
          todos={todos}
          now={now}
          isMustFinish={layer === GATE_LAYERS.MUST_FINISH}
          onGo={onGo}
          isSnoozingInitially={isSnoozingInitially}
        />
      );
  }

  return null;
};
